import { useState, useCallback, useRef } from 'react';
import { getTenantDetail, getTenantUsers } from '../api/superAdminApi';
import type { TenantDetail, TenantUserRow } from '../types';

interface UseOrganisationDetailResult {
  detail: TenantDetail | null;
  users: TenantUserRow[];
  detailLoading: boolean;
  usersLoading: boolean;
  detailError: string | null;
  usersError: string | null;
  loadDetail: (id: number) => void;
  loadUsers: (id: number) => void;
  updateDetailCache: (updater: (prev: TenantDetail) => TenantDetail) => void;
  clearDetail: () => void;
}

/** Cache TTL: 30 seconds. Stale entries are served but refresh is triggered. */
const CACHE_TTL_MS = 30_000;

interface CacheEntry {
  data: TenantDetail;
  timestamp: number;
}

/** Module-level cache stores recently loaded details keyed by tenant ID. */
const detailCache = new Map<number, CacheEntry>();

/** Invalidate a specific tenant's cache entry (call after mutations). */
export function invalidateTenantCache(id: number): void {
  detailCache.delete(id);
}

/** Clear all cached tenant details (call on logout/account change). */
export function clearTenantCache(): void {
  detailCache.clear();
}

export function useOrganisationDetail(): UseOrganisationDetailResult {
  const [detail, setDetail] = useState<TenantDetail | null>(null);
  const [users, setUsers] = useState<TenantUserRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const detailAbort = useRef<AbortController | null>(null);
  const usersAbort = useRef<AbortController | null>(null);
  const detailRequestId = useRef(0);
  const usersRequestId = useRef(0);

  const loadDetail = useCallback(async (id: number) => {
    // Serve from cache if not stale
    const cached = detailCache.get(id);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      setDetail(cached.data);
      setDetailError(null);
      return;
    }

    // Cancel previous in-flight detail request
    detailAbort.current?.abort();
    const controller = new AbortController();
    detailAbort.current = controller;
    const thisRequestId = ++detailRequestId.current;

    setDetailLoading(true);
    setDetailError(null);

    try {
      const data = await getTenantDetail(id, controller.signal);
      if (thisRequestId !== detailRequestId.current) return;

      const resolved = data.tenant;
      detailCache.set(id, { data: resolved, timestamp: Date.now() });
      setDetail(resolved);
    } catch (err: unknown) {
      if (thisRequestId !== detailRequestId.current) return;
      if (err instanceof Error && err.name === 'AbortError') return;

      const message = err instanceof Error ? err.message : 'Failed to load organisation';
      setDetailError(message);

      // Serve stale cache on error if available
      if (cached) {
        setDetail(cached.data);
      }
    } finally {
      if (thisRequestId === detailRequestId.current) {
        setDetailLoading(false);
      }
    }
  }, []);

  const loadUsers = useCallback(async (id: number) => {
    usersAbort.current?.abort();
    const controller = new AbortController();
    usersAbort.current = controller;
    const thisRequestId = ++usersRequestId.current;

    setUsersLoading(true);
    setUsersError(null);

    try {
      const data = await getTenantUsers(id, controller.signal);
      if (thisRequestId !== usersRequestId.current) return;
      setUsers(data.users || []);
    } catch (err: unknown) {
      if (thisRequestId !== usersRequestId.current) return;
      if (err instanceof Error && err.name === 'AbortError') return;
      setUsersError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      if (thisRequestId === usersRequestId.current) {
        setUsersLoading(false);
      }
    }
  }, []);

  const updateDetailCache = useCallback((updater: (prev: TenantDetail) => TenantDetail) => {
    setDetail((prev) => {
      if (!prev) return prev;
      const next = updater(prev);
      detailCache.set(next.id, { data: next, timestamp: Date.now() });
      return next;
    });
  }, []);

  const clearDetail = useCallback(() => {
    detailAbort.current?.abort();
    usersAbort.current?.abort();
    setDetail(null);
    setUsers([]);
    setDetailError(null);
    setUsersError(null);
  }, []);

  return {
    detail, users,
    detailLoading, usersLoading,
    detailError, usersError,
    loadDetail, loadUsers,
    updateDetailCache, clearDetail,
  };
}
